/**
 * Calibration Certificate (US-40351, Feature AB#38254).
 *
 * The certificate is an ATTACHMENT, rendered on click and never stored: a
 * "Calibration Certificate" row in each eligible vehicle's attachments on the
 * invoice page (shared/VehiclePhotos), and in the documentation area of the
 * ADAS Report page. These pure helpers decide where the rows go, kept out of
 * the components so they are unit-testable without React.
 *
 * SOURCE OF TRUTH IS THE API. The client-portal-api decides eligibility, with
 * or without a session, so share-link (email) visitors see the rows whenever
 * the document is eligible. Nothing here reads session flags. The API
 * re-checks on the download and answers 409 { reason } when it fails.
 *
 *   vehicle-report read: calibrationCertificate: { available }, a SIBLING of
 *                        vehicleReport in the response (VehicleReportController.ts, GET /:id)
 *   invoice read:        calibrationCertificates: [{ vehicleId, vehicleReportId, available: true }]
 *                        (eligible vehicles only; vehicleId = invoice.vehicles[i]._id)
 */

/**
 * ADAS Report page: whether the report carries a certificate. Only a literal
 * `available: true` counts (a string "true" does not); an absent or malformed
 * field reads as no certificate.
 *
 * @param {Object|null} cert  `calibrationCertificate` from the report read,
 *                            beside `vehicleReport` (not inside it)
 * @returns {Boolean}
 */
export const apiCertificateAvailability = cert => {
  if (!cert || typeof cert !== 'object') return false
  return cert.available === true
}

/**
 * Invoice page: the API's entry for one vehicle, or null when that vehicle has
 * no certificate. Matched on invoice.vehicles[i]._id; an entry must say
 * `available: true` literally.
 *
 * @param {Object|null} invoice
 * @param {String}      vehicleId  invoice.vehicles[i]._id
 * @returns {Object|null} { vehicleId, vehicleReportId, available }
 */
export const certificateForVehicle = (invoice, vehicleId) => {
  if (!vehicleId) return null
  const list = invoice && Array.isArray(invoice.calibrationCertificates) ? invoice.calibrationCertificates : []
  const entry = list.find(c => c && c.available === true && String(c.vehicleId) === String(vehicleId))
  return entry || null
}

/**
 * Body text for the error toast. The API's 409 carries `{ reason }` with no
 * `message`, and httpClient's handleResponseError only rewraps errors that
 * have a string `message` — a 409 therefore arrives as the raw axios error
 * whose `.message` is "Request failed with status code 409". Read the reason
 * off the response body first so the user sees why, not the status code.
 *
 * @param {Object|null} err      the rejection from the mutation
 * @param {String}      fallback shown when the error carries no usable text
 * @returns {String}
 */
export const certificateErrorMessage = (err, fallback) => {
  const data = err && err.response && err.response.data
  if (data && typeof data.reason === 'string' && data.reason) return data.reason
  if (data && typeof data.message === 'string' && data.message) return data.message
  if (err && typeof err.message === 'string' && err.message && !/^Request failed with status code/.test(err.message)) {
    return err.message
  }
  return fallback
}
